import type { Contract, ContractSigner, ContractTemplate, CreateTalentContractPayload } from "@/api/contract/models";
import { clientDelete, clientGet, clientPatch, clientPost } from "@/api/client";
import type { CrudQuery, PaginationResponse } from "@/interfaces/api";
import { api } from "@/services/api";
import { pick } from "lodash-es";

import { crud } from "../crud";
import type {
  CancelSchedulePayload,
  CreateNewCandidateSchedulePayload,
  CreatePhotoRenewalSchedulePayload,
  GenerateTalentCodeResponse,
  PhotoService,
  Photogeny,
  PhotogenyPayload,
  PhotographyResponse,
  PhotographerOption,
  PauseSchedulePayload,
  PreferredProducer,
  RejectedCandidatesResponse,
  RescheduleSchedulePayload,
  ScheduleDetail,
  ScheduledSchedule,
  SchedulePrefill,
  ScheduleStage,
  ScheduleStageCounts,
  ScheduleStatus,
  ScheduleTalentData,
  ScheduleSuggestion,
  ScheduleType,
  ScheduleTalentDataSection,
  TalentCandidateResult,
  TalentCandidateResultProducer,
  ScheduleProductionLog,
  SchedulePaymentMethods,
  SavePaymentMethodsPayload,
  SavePaymentMethodsResponse,
  UpdateTalentCandidateResultPayload,
  UpdateChangeOfClothesPayload,
  UpdateChangeOfClothesResponseDto,
  UpdateSchedulePresencePayload,
  UpdateScheduleStagePayload,
  UpdateScheduleTalentDataPayload,
} from "./models";

const { getParameterizedUrl } = crud<ScheduleDetail>("schedule", "Agenda");

export const scheduleCrud = {
  getPhotographyData: (scheduleId: string): Promise<PhotographyResponse> => {
    return clientGet({ path: `/schedule/${encodeURIComponent(scheduleId)}/photography-data` });
  },
  savePhotogenyEvaluation: (scheduleId: string, payload: PhotogenyPayload): Promise<Photogeny> => {
    return clientPatch({ path: `/schedule/${encodeURIComponent(scheduleId)}/photogeny`, payload });
  },
  getPhotographers: (scheduleId: string): Promise<PaginationResponse<PhotographerOption>> => {
    return clientGet({
      path: getParameterizedUrl({ sort: { page: 1, limit: 100 } }, scheduleId, "photographers"),
    });
  },
  listContractTemplates: (scheduleId: string): Promise<ContractTemplate[]> => {
    return clientGet({ path: `/schedule/${scheduleId}/contracts/templates` });
  },
  listContractSigners: (scheduleId: string): Promise<ContractSigner[]> => {
    return clientGet({ path: `/schedule/${scheduleId}/contracts/signers` });
  },
  createContract: (scheduleId: string, payload: CreateTalentContractPayload): Promise<Contract> => {
    return clientPost({ path: `/schedule/${scheduleId}/contracts`, payload });
  },

  getContracts: (scheduleId: string, query: CrudQuery): Promise<PaginationResponse<Contract>> => {
    return clientGet({ path: getParameterizedUrl(pick(query, "sort"), scheduleId, "contracts") });
  },
  getSignedContractPdfUrl: (scheduleId: string, contractId: string): Promise<{ url: string }> => {
    return clientGet({ path: `/schedule/${scheduleId}/contracts/${contractId}/signed-pdf` });
  },
  getOriginalContractPdfUrl: (scheduleId: string, contractId: string): Promise<{ url: string }> => {
    return clientGet({ path: `/schedule/${scheduleId}/contracts/${contractId}/original-pdf` });
  },
  getDetail: (scheduleId: string): Promise<ScheduleDetail> => {
    return clientGet<ScheduleDetail>({ path: `/schedule/${scheduleId}` });
  },

  getTalentData: (scheduleId: string): Promise<ScheduleTalentData> => {
    return clientGet<ScheduleTalentData>({ path: `/schedule/${scheduleId}/talent-data` });
  },

  getProductionLogs: (scheduleId: string): Promise<ScheduleProductionLog[]> => {
    return clientGet<ScheduleProductionLog[]>({ path: `/schedule/${scheduleId}/production/logs` });
  },

  getScheduleArtisticProductionChanges: (scheduleId: string): Promise<UpdateChangeOfClothesResponseDto> => {
    return clientGet<UpdateChangeOfClothesResponseDto>({
      path: `/schedule/${scheduleId}/artistic-production/changes`,
    });
  },

  updateScheduleArtisticProductionChanges: (
    scheduleId: string,
    payload: UpdateChangeOfClothesPayload,
  ): Promise<UpdateChangeOfClothesResponseDto> =>
    clientPatch<UpdateChangeOfClothesResponseDto>({
      path: `/schedule/${scheduleId}/artistic-production/changes`,
      payload,
    }),

  deleteScheduleArtisticProductionChange: (scheduleId: string, changeId: string): Promise<void> =>
    clientDelete<void>({ path: `/schedule/${scheduleId}/artistic-production/changes/${changeId}` }),

  getAgencying: (scheduleId: string): Promise<TalentCandidateResult | null> => {
    return clientGet<TalentCandidateResult | null>({ path: `/schedule/${scheduleId}/production/agencying` });
  },

  getPaymentMethods: async (scheduleId: string): Promise<SchedulePaymentMethods> => {
    const response = await api.get<SchedulePaymentMethods>(
      `/schedule/${encodeURIComponent(scheduleId)}/production/payment-methods`,
      {
        suppressGlobalErrorToast: true,
      },
    );
    return response.data;
  },

  updatePaymentMethods: (
    scheduleId: string,
    payload: SavePaymentMethodsPayload,
    files: Record<string, File | File[]>,
  ): Promise<SavePaymentMethodsResponse> => {
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    for (const [key, filesForField] of Object.entries(files)) {
      for (const file of Array.isArray(filesForField) ? filesForField : [filesForField]) {
        formData.append(key, file);
      }
    }
    return clientPatch<SavePaymentMethodsResponse>({
      path: `/schedule/${encodeURIComponent(scheduleId)}/production/payment-methods`,
      payload: formData,
      suppressGlobalErrorToast: true,
    });
  },

  getAgencyingProducers: (scheduleId: string): Promise<TalentCandidateResultProducer[]> => {
    return clientGet<TalentCandidateResultProducer[]>({ path: `/schedule/${scheduleId}/production/producers` });
  },

  updateAgencying: (
    scheduleId: string,
    payload: UpdateTalentCandidateResultPayload,
  ): Promise<TalentCandidateResult> => {
    return clientPatch<TalentCandidateResult>({ path: `/schedule/${scheduleId}/production/agencying`, payload });
  },

  generateTalentCode: (scheduleId: string): Promise<GenerateTalentCodeResponse> => {
    return clientPost<GenerateTalentCodeResponse>({ path: `/schedule/${scheduleId}/talent-code` });
  },

  start: (scheduleId: string): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/start` });
  },

  updateStage: (scheduleId: string, payload: UpdateScheduleStagePayload): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/stage`, payload });
  },

  updateTalentData: (
    scheduleId: string,
    section: ScheduleTalentDataSection,
    payload: UpdateScheduleTalentDataPayload,
  ): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/talent-data/${section}`, payload });
  },

  pause: (scheduleId: string, payload: PauseSchedulePayload): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/pause`, payload });
  },

  cancel: (scheduleId: string, payload: CancelSchedulePayload): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/cancel`, payload });
  },

  resume: (scheduleId: string): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/resume` });
  },

  unpause: (scheduleId: string): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/unpause` });
  },

  complete: (scheduleId: string): Promise<void> => {
    return clientPatch({ path: `/schedule/${scheduleId}/complete` });
  },
  sendContract: (scheduleId: string, contractId: string): Promise<Contract> => {
    return clientPost<Contract>({ path: `/schedule/${scheduleId}/contracts/${contractId}/send` });
  },
  cancelContract: (scheduleId: string, contractId: string): Promise<Contract> => {
    return clientPost<Contract>({ path: `/schedule/${scheduleId}/contracts/${contractId}/cancel` });
  },
  deleteContract: (scheduleId: string, contractId: string): Promise<void> => {
    return clientDelete<void>({ path: `/schedule/${scheduleId}/contracts/${contractId}` });
  },
  getProducers: (scheduleId: string, query: CrudQuery): Promise<PaginationResponse<PreferredProducer>> => {
    return clientGet({ path: getParameterizedUrl(query, scheduleId, "producers") });
  },
};

export const getScheduleTalentPrefill = async (talentCode: string, signal?: AbortSignal): Promise<SchedulePrefill> => {
  const response = await api.get<SchedulePrefill>(`/schedule/talent/${encodeURIComponent(talentCode)}`, { signal });
  return response.data;
};

export const getScheduleStageCounts = async (date?: string, signal?: AbortSignal): Promise<ScheduleStageCounts> => {
  const response = await api.get<ScheduleStageCounts>("/schedule/stage-counts", {
    params: { date },
    signal,
  });
  return response.data;
};

export const getScheduleSuggestions = async (
  query: { searchTerm: string; date?: string; stage?: ScheduleStage },
  signal?: AbortSignal,
): Promise<ScheduleSuggestion[]> => {
  const response = await api.get<ScheduleSuggestion[]>("/schedule/suggestions", {
    params: query,
    signal,
  });
  return response.data;
};

export const getRejectedCandidates = async (
  searchTerm: string,
  page: number,
  limit: number,
  producerIds: string[] = [],
  failureReasons: string[] = [],
  sorting?: { field: "resultDate" | "returnDate"; order: "ASC" | "DESC" },
): Promise<RejectedCandidatesResponse> => {
  const params = new URLSearchParams();
  if (searchTerm.trim()) params.set("searchTerm", searchTerm.trim());
  params.set("sort", JSON.stringify({ page, limit, ...sorting }));
  producerIds.forEach((producerId) => params.append("producerIds", producerId));
  failureReasons.forEach((failureReason) => params.append("failureReasons", failureReason));
  const response = await api.get<RejectedCandidatesResponse>("/schedule/rejected-candidates", { params });
  return response.data;
};

export const getScheduleAgenda = async (
  stage: ScheduleStage,
  date?: string,
  page = 1,
  limit = 8,
  scheduleTypes?: ScheduleType[],
  photoServices?: PhotoService[],
  lastStages?: ScheduleStage[],
  statuses?: ScheduleStatus[],
  scheduleDateOrder?: "ASC" | "DESC",
  signal?: AbortSignal,
): Promise<PaginationResponse<ScheduledSchedule>> => {
  const response = await api.get<PaginationResponse<ScheduledSchedule>>("/schedule", {
    params: {
      stage,
      date,
      scheduleTypes: scheduleTypes === undefined ? undefined : JSON.stringify(scheduleTypes),
      photoServices: photoServices === undefined ? undefined : JSON.stringify(photoServices),
      lastStages: lastStages === undefined ? undefined : JSON.stringify(lastStages),
      statuses: statuses === undefined ? undefined : JSON.stringify(statuses),
      sort: JSON.stringify({ page, limit, ...(scheduleDateOrder ? { scheduleDateOrder } : {}) }),
    },
    signal,
  });
  return response.data;
};

export const getScheduleRegistrationLinkCode = async (scheduleId: string): Promise<{ infoUpdateCode: string }> => {
  const response = await api.post<{ infoUpdateCode: string }>(
    `/schedule/${encodeURIComponent(scheduleId)}/registration-link-code`,
  );
  return response.data;
};

export const deleteSchedule = async (scheduleId: string): Promise<void> => {
  await api.delete(`/schedule/${encodeURIComponent(scheduleId)}`);
};

export const updateSchedulePresence = async (
  scheduleId: string,
  payload: UpdateSchedulePresencePayload,
): Promise<void> => {
  await api.patch(`/schedule/${encodeURIComponent(scheduleId)}/presence`, payload);
};

export const rescheduleSchedule = async (
  scheduleId: string,
  payload: RescheduleSchedulePayload,
): Promise<{ id: string }> => {
  const response = await api.patch<{ id: string }>(`/schedule/${encodeURIComponent(scheduleId)}/reschedule`, payload);
  return response.data;
};

export const createPhotoRenewalSchedule = async (
  payload: CreatePhotoRenewalSchedulePayload,
): Promise<{ id: string }> => {
  const response = await api.post<{ id: string }>("/schedule/photo-renewal", payload);
  return response.data;
};

export const createNewCandidateSchedule = async (
  payload: CreateNewCandidateSchedulePayload,
): Promise<{ id: string }> => {
  const response = await api.post<{ id: string }>("/schedule/new-candidate", payload);
  return response.data;
};
